import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { XIcon, SearchIcon, CollectionIcon } from "@heroicons/react/outline";
import type { AvailableParcours, ParcoursGroup } from "../types/groupTypes";
import { getEntityColor } from "../utils/comparisonColors";

interface CreateParcoursGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateGroup: (name: string, parcoursIds: number[]) => boolean;
  availableParcours: AvailableParcours[];
  existingGroups: ParcoursGroup[];
}

const CreateParcoursGroupModal = ({
  isOpen,
  onClose,
  onCreateGroup,
  availableParcours,
  existingGroups,
}: CreateParcoursGroupModalProps) => {
  const { t } = useTranslation();

  const [groupName, setGroupName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedParcoursIds, setSelectedParcoursIds] = useState<Set<number>>(new Set());

  const filteredParcours = useMemo(() => {
    if (!searchValue) return availableParcours;
    const search = searchValue.toLowerCase();
    return availableParcours.filter((p) => p.name.toLowerCase().includes(search));
  }, [availableParcours, searchValue]);

  const getParcoursGroups = (parcoursId: number) => {
    const results: { group: ParcoursGroup; index: number }[] = [];
    for (let i = 0; i < existingGroups.length; i++) {
      if (existingGroups[i].parcoursIds.includes(parcoursId)) {
        results.push({ group: existingGroups[i], index: i });
      }
    }
    return results;
  };

  const handleToggleParcours = (parcoursId: number) => {
    const newSet = new Set(selectedParcoursIds);
    if (newSet.has(parcoursId)) {
      newSet.delete(parcoursId);
    } else {
      newSet.add(parcoursId);
    }
    setSelectedParcoursIds(newSet);
  };

  const handleSelectAll = () => {
    if (selectedParcoursIds.size === filteredParcours.length) {
      setSelectedParcoursIds(new Set());
    } else {
      setSelectedParcoursIds(new Set(filteredParcours.map((p) => p.id)));
    }
  };

  const handleCreate = () => {
    if (!groupName.trim()) return;
    if (selectedParcoursIds.size === 0) return;

    const success = onCreateGroup(groupName.trim(), Array.from(selectedParcoursIds));

    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setGroupName("");
    setSearchValue("");
    setSelectedParcoursIds(new Set());
    onClose();
  };

  const isCreateDisabled = useMemo(() => {
    if (!groupName.trim()) return true;
    return selectedParcoursIds.size === 0;
  }, [groupName, selectedParcoursIds.size]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <CollectionIcon className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t("statsNew.comparateur.parcoursGroups.createGroup")}
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("statsNew.comparateur.parcoursGroups.groupName")}
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t("statsNew.comparateur.parcoursGroups.groupNamePlaceholder")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div className="relative mb-3">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t("statsNew.comparateur.parcoursGroups.searchParcours")}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">
              {selectedParcoursIds.size} {t("statsNew.comparateur.groups.selected")}
            </span>
            <button
              onClick={handleSelectAll}
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              {selectedParcoursIds.size === filteredParcours.length
                ? t("statsNew.comparateur.groups.deselectAll")
                : t("statsNew.comparateur.groups.selectAll")}
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg h-64 overflow-y-auto">
            {filteredParcours.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {t("statsNew.comparateur.parcoursGroups.noParcoursFound")}
              </div>
            ) : (
              filteredParcours.map((parcours) => {
                const groupsInfo = getParcoursGroups(parcours.id);
                return (
                  <label
                    key={parcours.id}
                    className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedParcoursIds.has(parcours.id)}
                      onChange={() => handleToggleParcours(parcours.id)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {parcours.name}
                        </p>
                        {groupsInfo.map((info) => (
                          <span
                            key={info.group.id}
                            className="text-[10px] px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
                            style={{ backgroundColor: getEntityColor(info.index).primary }}
                          >
                            {info.group.name}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        {parcours.totalSessions} sessions
                        {parcours.averageScore !== null &&
                          ` • moy. ${parcours.averageScore}/${parcours.averageScoreMax} pts`}
                      </p>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {t("general.cancel")}
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreateDisabled}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              isCreateDisabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {t("statsNew.comparateur.groups.create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateParcoursGroupModal;
